import { useEffect, useMemo, useState } from "react";
import { MessageSquare, Send, Tag, Plus, WifiOff, Info } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { fmtDate, uuid, vals } from "@/lib/format";
import { BigButton, Chip, Field, PageTitle, inputCls } from "@/components/ui-kit";

const SEGMENTS = {
  all: () => true,
  debtors: (c) => c.balance > 0,
  loyal: (c) => (c.points || 0) >= 10,
  inactive: (c) => !c.last_purchase_at || Date.now() - new Date(c.last_purchase_at) > 30 * 86400000,
};

function Campaign() {
  const { view, t, money, online } = useApp();
  const [seg, setSeg] = useState("all");
  const [selected, setSelected] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState(null);
  const list = useMemo(() => vals(view.customers).filter(SEGMENTS[seg]), [view.customers, seg]);
  useEffect(() => { setSelected(list.filter((c) => c.phone).map((c) => c.id)); }, [list]);
  useEffect(() => { if (online) api.get("/").then((r) => setLive(r.data.whatsapp_live)).catch(() => {}); }, [online]);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/campaigns/send", { customer_ids: selected, message: msg, kind: seg === "debtors" ? "reminder" : "promo" });
      toast.success(`${data.mocked ? t("sent_mocked") : t("sent")} (${data.sent}/${data.count})`);
      setMsg("");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-4">
      {live === false && <div className="rounded-xl bg-[#3498DB]/10 text-[#2471A3] p-3 text-sm font-bold flex gap-2" data-testid="whatsapp-mocked-banner"><Info className="w-5 h-5 shrink-0" />{t("whatsapp_mocked_note")}</div>}
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {Object.keys(SEGMENTS).map((k) => <Chip key={k} active={seg === k} onClick={() => setSeg(k)} data-testid={`segment-${k}`}>{t(`seg_${k}`)}</Chip>)}
      </div>
      <div className="rounded-2xl bg-white border border-border divide-y divide-border max-h-72 overflow-y-auto" data-testid="campaign-customer-list">
        {list.length === 0 && <p className="p-4 text-muted-foreground">{t("no_customers")}</p>}
        {list.map((c) => (
          <label key={c.id} className="flex items-center gap-3 p-3 min-h-[52px] cursor-pointer">
            <Checkbox checked={selected.includes(c.id)} disabled={!c.phone} onCheckedChange={() => toggle(c.id)} data-testid={`campaign-select-${c.id}`} className="w-6 h-6" />
            <span className="flex-1 font-bold">{c.name} <span className="text-sm text-muted-foreground font-normal">{c.phone || t("no_phone")}</span></span>
            {c.balance > 0 && <span className="text-sm font-bold text-[#E67E22]">{money(c.balance)}</span>}
          </label>
        ))}
      </div>
      <div className="flex gap-2 flex-wrap">
        {["tpl_promo", "tpl_reminder", "tpl_thanks"].map((k) => <Chip key={k} onClick={() => setMsg(t(k))} data-testid={`template-${k}`}><MessageSquare className="w-4 h-4" />{t(`${k}_label`)}</Chip>)}
      </div>
      <Field label={`${t("message")} · {name} {balance} {shop}`}>
        <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={4} className={`${inputCls} h-auto py-3`} data-testid="campaign-message-input" />
      </Field>
      <BigButton variant="green" disabled={!online || busy || !msg.trim() || !selected.length} onClick={send} className="w-full" data-testid="whatsapp-campaign-send-btn">
        {online ? <Send className="w-5 h-5" /> : <WifiOff className="w-5 h-5" />}{online ? t("send_whatsapp", { n: selected.length }) : t("need_internet")}
      </BigButton>
    </div>
  );
}

function Promotions() {
  const { view, t, dispatch } = useApp();
  const [name, setName] = useState("");
  const [percent, setPercent] = useState("");
  const promos = vals(view.promotions);
  const add = () => {
    if (name.trim() && Number(percent) > 0 && dispatch("promotion.upsert", { id: uuid(), name: name.trim(), percent: Number(percent), active: true })) {
      setName(""); setPercent("");
    }
  };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-[1fr_90px_auto] gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("promo_name")} className={inputCls} data-testid="promo-name-input" />
        <input dir="ltr" inputMode="decimal" value={percent} onChange={(e) => setPercent(e.target.value)} placeholder="%" className={`${inputCls} text-center`} data-testid="promo-percent-input" />
        <BigButton onClick={add} data-testid="promo-add-btn"><Plus className="w-5 h-5" /></BigButton>
      </div>
      {promos.map((p) => (
        <div key={p.id} className="rounded-xl bg-white border border-border p-3 flex items-center gap-3" data-testid="promo-row">
          <Tag className="w-5 h-5 text-primary" />
          <span className="flex-1 font-bold">{p.name} <span className="text-[#1F9D55]">-{p.percent}%</span></span>
          <Switch checked={!!p.active} onCheckedChange={(v) => dispatch("promotion.upsert", { id: p.id, active: v })} data-testid={`promo-toggle-${p.id}`} />
        </div>
      ))}
      <p className="text-sm text-muted-foreground">{t("loyalty_note", { n: view.shop?.loyalty_per || 100 })}</p>
    </div>
  );
}

export default function Marketing() {
  const { view, t, lang } = useApp();
  const camps = vals(view.campaigns).sort((a, b) => b.created_at.localeCompare(a.created_at));
  return (
    <div>
      <PageTitle testid="marketing-title">{t("marketing")}</PageTitle>
      <Tabs defaultValue="campaign">
        <TabsList className="w-full h-12 mb-4">
          <TabsTrigger value="campaign" className="flex-1 h-10 font-bold" data-testid="marketing-tab-campaign">{t("whatsapp")}</TabsTrigger>
          <TabsTrigger value="promos" className="flex-1 h-10 font-bold" data-testid="marketing-tab-promos">{t("promotions")}</TabsTrigger>
          <TabsTrigger value="history" className="flex-1 h-10 font-bold" data-testid="marketing-tab-history">{t("history")}</TabsTrigger>
        </TabsList>
        <TabsContent value="campaign"><Campaign /></TabsContent>
        <TabsContent value="promos"><Promotions /></TabsContent>
        <TabsContent value="history" className="space-y-2">
          {camps.length === 0 && <p className="text-muted-foreground">{t("no_history")}</p>}
          {camps.map((c) => (
            <div key={c.id} className="rounded-xl bg-white border border-border p-3" data-testid="campaign-history-row">
              <div className="flex justify-between gap-2 font-bold">
                <span>{fmtDate(c.created_at, lang)}</span>
                <span className="flex gap-2">{c.mocked && <span className="text-xs px-2 py-0.5 rounded bg-[#3498DB]/10 text-[#2471A3]">MOCKED</span>}{c.sent}/{c.count}</span>
              </div>
              <p className="text-sm text-muted-foreground line-clamp-2">{c.message}</p>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
